import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

/* PageHeader — guide §4 page anatomy: breadcrumbs, H1 page title, optional
   subtitle, and right-aligned action slot. Use on every top-level route. */

export interface PageBreadcrumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  breadcrumbs?: PageBreadcrumb[];
  actions?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("mb-6 flex flex-col gap-3", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-1 text-xs text-slate">
            {breadcrumbs.map((crumb, idx) => {
              const last = idx === breadcrumbs.length - 1;
              return (
                <li key={`${crumb.label}-${idx}`} className="flex items-center gap-1">
                  {crumb.to && !last ? (
                    <Link to={crumb.to} className="hover:text-navy">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={last ? "text-navy" : undefined}>
                      {crumb.label}
                    </span>
                  )}
                  {!last && (
                    <ChevronRight size={12} aria-hidden="true" className="text-slate-300" />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-navy md:text-[32px] md:leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-slate">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="page-actions flex items-center gap-2">{actions}</div>
        )}
      </div>
    </header>
  );
}
